import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { LINE_ITEM_STATUS_ORDER, SOURCE_LABEL, WORKSHOP_LINE_STATUSES, STATUS } from '../constants';
import { extractColour, colourHex, DEFAULT_COLOURS } from '../lib/colours';
import { getColourPalette } from '../services/configService';
import { buildOrderUrl } from '../lib/platformLinks';

const fmtDate = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
    : '';

const UNKNOWN = 'Unknown colour';
// Recompute live from the variation so palette/extraction changes apply without a
// re-import (the stored lineItem.colour may pre-date smarter extraction).
const colourOf = (li, palette) =>
  extractColour(`${li.variation || ''} ${li.product || ''}`, palette) || UNKNOWN;

// Readable text colour for a coloured chip background.
const textOn = (hex) => {
  const h = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return r * 0.299 + g * 0.587 + b * 0.114 > 150 ? '#000' : '#fff';
};

/**
 * Workshop cutting view: line items across the active orders, grouped by COLOUR.
 * A status filter picks what counts as "to make"; colour chips (all selected by
 * default, only the colours present) filter which colours show. Client-side over
 * the loaded active set (line items aren't queryable).
 */
export default function CuttingView({ orders = [], onLineStatus, onOpenOrder }) {
  const [statusFilter, setStatusFilter] = useState(() => new Set(WORKSHOP_LINE_STATUSES));
  const [deselected, setDeselected] = useState(() => new Set()); // colours hidden (empty = all shown)
  const [palette, setPalette] = useState(DEFAULT_COLOURS);

  useEffect(() => {
    getColourPalette().then(setPalette);
  }, []);

  const toggleStatus = (s) =>
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const toggleColour = (c) =>
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  // Status-filtered line items, tagged with their colour.
  const items = useMemo(() => {
    const out = [];
    orders.forEach((order) => {
      (order.lineItems || []).forEach((li) => {
        // Embroidery lines aren't cut — they live in the Embroidery tab.
        if (li.isEmbroidery) return;
        if (statusFilter.has(li.status || STATUS.UNCUT)) {
          out.push({ order, li, colour: colourOf(li, palette) });
        }
      });
    });
    return out;
  }, [orders, statusFilter, palette]);

  // Distinct colours present in the data set (these drive the chips).
  const colours = useMemo(() => {
    const set = new Set(items.map((r) => r.colour));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [items]);

  // Colour-filtered, grouped by colour.
  const groups = useMemo(() => {
    const visible = items.filter((r) => !deselected.has(r.colour));
    visible.sort(
      (a, b) => a.colour.localeCompare(b.colour) || (a.li.product || '').localeCompare(b.li.product || '')
    );
    const out = [];
    let current = null;
    visible.forEach((row) => {
      if (!current || current.key !== row.colour) {
        current = { key: row.colour, rows: [] };
        out.push(current);
      }
      current.rows.push(row);
    });
    return out;
  }, [items, deselected]);

  const total = groups.reduce((n, g) => n + g.rows.length, 0);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">Show:</Typography>
        {LINE_ITEM_STATUS_ORDER.map((s) => (
          <Chip
            key={s}
            label={s}
            size="small"
            color={statusFilter.has(s) ? 'primary' : 'default'}
            variant={statusFilter.has(s) ? 'filled' : 'outlined'}
            onClick={() => toggleStatus(s)}
          />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {total} item{total === 1 ? '' : 's'} to make
        </Typography>
      </Stack>

      {/* Colour chips — only the colours present; all selected to start. */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">Colours:</Typography>
        <Chip label="All" size="small" variant="outlined" onClick={() => setDeselected(new Set())} />
        <Chip label="None" size="small" variant="outlined" onClick={() => setDeselected(new Set(colours))} />
        {colours.map((c) => {
          const on = !deselected.has(c);
          const hex = colourHex(c, palette);
          return (
            <Chip
              key={c}
              label={c}
              size="small"
              onClick={() => toggleColour(c)}
              variant={on ? 'filled' : 'outlined'}
              sx={{
                ...(on
                  ? { backgroundColor: hex, color: textOn(hex), '&:hover': { backgroundColor: hex } }
                  : { borderColor: hex, color: 'text.secondary' }),
              }}
            />
          );
        })}
        {colours.length === 0 && (
          <Typography variant="caption" color="text.secondary">none</Typography>
        )}
      </Stack>

      <Table size="small" sx={{ '& td, & th': { fontSize: '0.8rem', py: 0.5 } }}>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Status</TableCell>
            <TableCell>Product</TableCell>
            <TableCell>Variation</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell>Buyer</TableCell>
            <TableCell>Order</TableCell>
            <TableCell>Post by</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              <TableRow>
                <TableCell colSpan={9} sx={{ bgcolor: 'action.hover', fontWeight: 700 }}>
                  <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '2px', backgroundColor: colourHex(g.key, palette), mr: 1, verticalAlign: 'middle', border: '1px solid rgba(0,0,0,0.2)' }} />
                  {g.key} · {g.rows.length}
                </TableCell>
              </TableRow>
              {g.rows.map(({ order, li }) => {
                const url = buildOrderUrl(order);
                return (
                <TableRow key={`${order.id}:${li.lineId}`} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {onOpenOrder && (
                      <Tooltip title="Order details / edit" arrow>
                        <IconButton size="small" onClick={() => onOpenOrder(order.id)}>
                          <EditNoteIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {url && (
                      <Tooltip title="Open on the platform" arrow>
                        <IconButton size="small" onClick={() => window.open(url, '_blank', 'noopener')}>
                          <OpenInNewIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      disableUnderline
                      value={LINE_ITEM_STATUS_ORDER.includes(li.status) ? li.status : STATUS.UNCUT}
                      onChange={(e) => onLineStatus(order, li.lineId, e.target.value)}
                      sx={{ fontSize: '0.8rem', minWidth: 110 }}
                    >
                      {LINE_ITEM_STATUS_ORDER.map((s) => (
                        <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>{s}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>{li.product}</TableCell>
                  <TableCell>{li.variation}</TableCell>
                  <TableCell align="right">{li.quantity}</TableCell>
                  <TableCell>{order.buyer?.name}</TableCell>
                  <TableCell>{SOURCE_LABEL[order.source] || order.source} {order.platformOrderId}</TableCell>
                  <TableCell>{fmtDate(order.dates?.postByEffective)}</TableCell>
                  <TableCell>{li.buyerNotes}</TableCell>
                </TableRow>
                );
              })}
            </React.Fragment>
          ))}
          {total === 0 && (
            <TableRow>
              <TableCell colSpan={9}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No line items match the selected statuses/colours.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
